/**
 * Snapshot buffering and interpolation.
 *
 * The arena sends state 20 times a second but the client draws 60+ times a second.
 * Rendering the newest snapshot directly would show 20Hz stutter, so the client
 * deliberately renders *slightly in the past* — `INTERPOLATION_DELAY_MS` behind the
 * stream — and slides between the two snapshots that straddle that moment. The delay
 * buys a cushion: one late or dropped packet is covered by the next.
 *
 * Crucially the timeline is the **server's**, taken from each snapshot's tick, not
 * the moment the packet happened to arrive. Arrival times carry network jitter, and
 * interpolating against them feeds that jitter straight into the motion: two packets
 * arriving 10ms apart after a 90ms gap would make the world lurch, then crawl.
 *
 * Everything here is pure, so it can be tested without a socket.
 */

export type NetPlayer = {
  i: string;
  n: string;
  c: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  o: number;
  s: number;
  a: number;
  sh: number;
  bo: number;
  cd: number;
  sp: number;
  /** Seconds of surge cooldown left; the tier itself is read off the orb count. */
  sc: number;
};

export type NetShot = {
  i: number;
  o: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  st: number;
  /** The ring this orb was fired from: 0 ordinary, 1-3 charged. */
  sg: number;
  c: string;
};

export type NetEnergy = { i: number; x: number; y: number; c: string; z: number; o: string };

export type Snapshot = {
  tick: number;
  timeLeft: number;
  elapsed: number;
  /** Who the room is hunting, from the server. Never interpolated — it is an id. */
  bounty?: string | null;
  ack: number;
  players: NetPlayer[];
  shots: NetShot[];
  /** Client clock reading when this snapshot arrived. Used only to estimate offset. */
  receivedAt: number;
  /** When the server produced this state, in server milliseconds. */
  serverTime: number;
};

/** How far behind the newest packet the client renders. Two snapshots at 20Hz. */
export const INTERPOLATION_DELAY_MS = 100;
/** Older snapshots than this are useless for interpolation and are dropped. */
export const SNAPSHOT_BUFFER_MS = 1000;

export function pushSnapshot(buffer: Snapshot[], snapshot: Snapshot): Snapshot[] {
  // Out-of-order delivery is normal; keep the buffer sorted by tick so the pair
  // search below can rely on ordering.
  if (buffer.length > 0 && snapshot.tick <= buffer[buffer.length - 1].tick) {
    if (buffer.some((entry) => entry.tick === snapshot.tick)) return buffer;
    const at = buffer.findIndex((entry) => entry.tick > snapshot.tick);
    buffer.splice(at < 0 ? buffer.length : at, 0, snapshot);
  } else {
    buffer.push(snapshot);
  }

  const newest = buffer[buffer.length - 1].serverTime;
  while (buffer.length > 2 && newest - buffer[0].serverTime > SNAPSHOT_BUFFER_MS) buffer.shift();
  return buffer;
}

export type SnapshotPair = { from: Snapshot; to: Snapshot; alpha: number };

/**
 * Picks the two snapshots surrounding the render moment. Returns `alpha` clamped to
 * [0,1]: at 1 with a single snapshot, or when the buffer has fallen behind and the
 * client has nothing newer to slide toward.
 */
export function pickSnapshotPair(buffer: Snapshot[], renderAt: number): SnapshotPair | null {
  if (buffer.length === 0) return null;
  if (buffer.length === 1) return { from: buffer[0], to: buffer[0], alpha: 1 };

  for (let index = buffer.length - 1; index > 0; index -= 1) {
    const to = buffer[index];
    const from = buffer[index - 1];
    if (from.serverTime <= renderAt && renderAt <= to.serverTime) {
      const span = to.serverTime - from.serverTime;
      const alpha = span <= 0 ? 1 : (renderAt - from.serverTime) / span;
      return { from, to, alpha: Math.max(0, Math.min(1, alpha)) };
    }
  }

  // Render time is outside the buffer: either we are still filling it (render the
  // oldest we have) or the stream stalled (hold on the newest).
  const oldest = buffer[0];
  if (renderAt < oldest.serverTime) return { from: oldest, to: oldest, alpha: 1 };
  const newest = buffer[buffer.length - 1];
  return { from: newest, to: newest, alpha: 1 };
}

function lerp(from: number, to: number, alpha: number) {
  return from + (to - from) * alpha;
}

/**
 * Beyond this much travel between two snapshots, the entity did not move — it was
 * placed. A boosting core covers roughly 13px and the fastest shot roughly 28px in
 * one 50ms interval, so anything past this is a respawn or a fresh spawn.
 */
export const TELEPORT_DISTANCE = 160;

/**
 * A respawn moves a core from where it died to somewhere else entirely. Blending
 * across that draws it briefly at the corpse and then streaks it over the map — the
 * "the place it died keeps blinking" report. Discontinuities snap instead.
 */
function isDiscontinuous(before: { x: number; y: number }, after: { x: number; y: number }) {
  return Math.hypot(after.x - before.x, after.y - before.y) > TELEPORT_DISTANCE;
}

/**
 * Interpolated view of the world, ready to render. Energy is supplied by the caller
 * rather than interpolated: particles are static between pickups, and the server
 * only sends the ones that changed.
 */
export type InterpolatedWorld = {
  tick: number;
  timeLeft: number;
  elapsed: number;
  bounty: string | null;
  players: NetPlayer[];
  shots: NetShot[];
  energy: NetEnergy[];
};

/**
 * Blends two snapshots. Entities present in `to` are interpolated from their `from`
 * counterpart when there is one, and otherwise appear at their `to` position — a
 * newly spawned core should not slide in from a stale location.
 */
/**
 * Cores are drawn in the past, because guessing where a person will steer is a guess
 * that gets corrected in front of you. A shot is different: it is a straight line at
 * a constant speed with nobody's mind behind it, so where it will be is not a guess.
 * Drawing shots in the past instead put them 45px behind the truth at the default
 * speed — a whole core's width — which is why an orb could look like it missed and
 * still kill you. They are carried forward to now.
 */
export function interpolate(pair: SnapshotPair, extrapolateMs = 0): InterpolatedWorld {
  const { from, to, alpha } = pair;
  const ahead = extrapolateMs / 1000;
  const carried = (shot: NetShot) => (ahead <= 0
    ? shot
    : { ...shot, x: shot.x + shot.vx * ahead, y: shot.y + shot.vy * ahead });

  if (from === to || alpha >= 1) {
    return {
      tick: to.tick,
      timeLeft: to.timeLeft,
      elapsed: to.elapsed,
      // Taken from the newer snapshot whole: a mark half-way between two cores is
      // not a thing, so this steps rather than blends.
      bounty: to.bounty ?? null,
      players: to.players,
      shots: to.shots.map(carried),
      energy: EMPTY_ENERGY,
    };
  }

  const previousPlayers = new Map(from.players.map((player) => [player.i, player]));
  const players = to.players.map((player) => {
    const before = previousPlayers.get(player.i);
    if (!before) return player;
    // Coming back from the dead is a placement, not a movement.
    if (before.a === 0 && player.a === 1) return player;
    if (isDiscontinuous(before, player)) return player;
    return {
      ...player,
      x: lerp(before.x, player.x, alpha),
      y: lerp(before.y, player.y, alpha),
      vx: lerp(before.vx, player.vx, alpha),
      vy: lerp(before.vy, player.vy, alpha),
    };
  });

  const previousShots = new Map(from.shots.map((shot) => [shot.i, shot]));
  const shots = to.shots.map((shot) => {
    const before = previousShots.get(shot.i);
    if (!before || isDiscontinuous(before, shot)) return carried(shot);
    return {
      ...shot,
      x: lerp(before.x, shot.x, alpha) + shot.vx * ahead,
      y: lerp(before.y, shot.y, alpha) + shot.vy * ahead,
    };
  });

  return {
    tick: to.tick,
    timeLeft: lerp(from.timeLeft, to.timeLeft, alpha),
    elapsed: lerp(from.elapsed ?? 0, to.elapsed ?? 0, alpha),
    bounty: to.bounty ?? null,
    players,
    shots,
    energy: EMPTY_ENERGY,
  };
}

const EMPTY_ENERGY: NetEnergy[] = [];

/**
 * Tracks how far the client clock runs ahead of server time. The smallest observed
 * gap is the best estimate — that packet had the least queueing delay — so the
 * estimate snaps down immediately and drifts up slowly to follow real clock skew
 * without chasing every late packet.
 */
export function updateClockOffset(current: number | null, receivedAt: number, serverTime: number) {
  const observed = receivedAt - serverTime;
  if (current === null) return observed;
  if (observed < current) return observed;
  return current + (observed - current) * 0.01;
}

/**
 * Buffer plus clock in, renderable world out. `renderAt` is on the server timeline:
 * the local clock translated by the measured offset, then held back by the delay.
 */
export function sampleWorld(
  buffer: Snapshot[],
  now: number,
  clockOffset: number,
  delayMs = INTERPOLATION_DELAY_MS,
): InterpolatedWorld | null {
  if (buffer.length === 0) return null;
  const pair = pickSnapshotPair(buffer, now - clockOffset - delayMs);
  // Cores are sampled `delayMs` in the past; shots are carried back to the present.
  return pair ? interpolate(pair, delayMs) : null;
}
